/**
 * WashProsLogo — official WashPros brand lockup for the public/main app surface.
 *
 * Phase 7g.15.89 (Jun 2026) — REPLACES the generic two-droplet
 * approximation with the OFFICIAL WashPros brand mark (Lucide
 * outlined two-droplets on the cyan→blue circular gradient — same
 * geometry as `/brand-downloads/WashPros_Icon.svg`). The mark is
 * inlined as JSX so it ships inside the AAB WebView assets and never
 * depends on a network or static-file fetch (broken-asset bug from
 * Build 14 stays fixed).
 *
 * Rick spec (Phase 7g.15.89 → Phase 7g.15.90) — final header shows
 * only the two trademark-marked lines stacked under the icon:
 *   1. WashPros™                          (wordmark)
 *   2. The Car Wash Concierge Company™    (single tagline)
 *
 * The Wax & Shine Headquarters™ marketing line lives only in the
 * hero/marketing surfaces (not the top-left logo lockup) per
 * Rick 2026-06-07 — removed to stop the duplicate tagline noise.
 */
import React from "react";
import { useTranslation } from "react-i18next";

// Official WashPros brand mark — Lucide "droplets" outline icon (same
// path data as /app/frontend/public/brand-downloads/WashPros_Icon.svg)
// rendered on the official cyan→blue vertical gradient circle.
// Inlined into the JS bundle so it ships inside the AAB WebView
// assets with zero file-system or network dependency.
const WashProsIconSvg = ({ className = "", testId }) => {
  // i18n sweep 2026-06-11 — localized a11y label (was hardcoded EN).
  const { t } = useTranslation();
  return (
  <svg
    viewBox="0 0 96 96"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label={t("shared.washpros_logo_aria")}
    data-testid={testId}
  >
    <defs>
      <linearGradient id="washpros-icon-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#06B6D4" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
    </defs>
    {/* Official cyan→blue circular gradient background */}
    <circle cx="48" cy="48" r="48" fill="url(#washpros-icon-bg)" />
    {/* Lucide droplets — outlined two-droplet motif (identical geometry
        to WashPros_Icon.svg, scaled to fit the 96x96 viewBox). */}
    <g
      transform="translate(21.6 21.6) scale(2.2)"
      fill="none"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z" />
      <path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97" />
    </g>
  </svg>
  );
};

export const WashProsLogo = ({
  size = "md",            // "sm" | "md" | "lg"
  showWordmark = true,
  showTagline = true,
  className = "",
  testId = "washpros-logo",
}) => {
  const sizes = {
    sm: { icon: "w-8 h-8",   word: "text-base" },
    md: { icon: "w-10 h-10", word: "text-xl" },
    lg: { icon: "w-12 h-12", word: "text-2xl" },
  };
  const cfg = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 sm:gap-3 ${className}`} data-testid={testId}>
      {/* Official inline brand mark — ships inside the JS bundle, no /brand-assets/ fetch. */}
      <WashProsIconSvg
        className={`${cfg.icon} rounded-full shadow-lg shadow-cyan-500/30 dark:shadow-cyan-500/30 shrink-0`}
        testId={`${testId}-mark`}
      />
      {showWordmark && (
        <div className="leading-tight">
          <span
            className={`${cfg.word} font-bold tracking-tight text-foreground block`}
            data-testid={`${testId}-wordmark`}
          >
            WashPros<sup className="text-[0.7em] ml-0.5 font-semibold align-super" data-testid={`${testId}-tm`}>™</sup>
          </span>
          {showTagline && (
            <p
              className="text-[10px] sm:text-[11px] text-cyan-500 dark:text-cyan-400 font-medium leading-snug"
              data-testid={`${testId}-tagline-concierge`}
            >
              The Car Wash Concierge Company<sup className="text-[0.7em] font-semibold align-super">™</sup>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default WashProsLogo;
